<script setup lang="ts">
import {
  EDITOR_FILE_BROWSER_FILTER_PATTERNS,
  EDITOR_FILE_BROWSER_SCOPES,
  buildEditorFileBrowserBreadcrumbs,
  editorFileBrowserEntryMatchesFilter,
  inferEditorFileBrowserScope,
  initialEditorFileBrowserFolder,
  isEditorFileBrowserDirectory,
  resolveEditorFileBrowserScope,
  toEditorFileBrowserLoadingPath,
  toEditorFileBrowserStoredPath,
  visibleEditorFileBrowserEntries,
  type EditorFileBrowserEntry,
  type EditorFileBrowserFilter,
  type EditorFileBrowserSelection,
  type EditorFileBrowserScope,
} from "#bb/lib/editorFileBrowser";
import {
  AUTHORING_PATH_FIELD_MODE_DEFINITIONS,
  toFileApiPath,
  type AuthoringPathFieldMode,
  isResourcePathDescendant,
  toResourceRelativePath,
  toStoredResourceSelectionPath,
} from "#bb/lib/pathContract";

interface Props {
  path?: string | null;
  mode?: AuthoringPathFieldMode;
  scopes?: readonly EditorFileBrowserScope[];
  defaultScope?: EditorFileBrowserScope;
  previewCandidateId?: string | null;
  contentTypeFilter?: EditorFileBrowserFilter;
  includeContentTypes?: readonly string[];
  excludeContentTypes?: readonly string[];
  title?: string | null;
  description?: string | null;
  resourceDefinitionPath?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  path: null,
  mode: "existing-file",
  scopes: () => [...EDITOR_FILE_BROWSER_SCOPES],
  defaultScope: "site",
  previewCandidateId: null,
  contentTypeFilter: "all",
  includeContentTypes: () => [],
  excludeContentTypes: () => [],
  title: null,
  description: null,
  resourceDefinitionPath: null,
});

const emit = defineEmits<{
  select: [path: string, selection: EditorFileBrowserSelection];
  cancel: [];
}>();

const open = defineModel<boolean>("open", { default: false });
const { siteId } = useSite();
const fileEntriesApi = useFileEntries(siteId);

const selectedScope = shallowRef<EditorFileBrowserScope>(props.defaultScope);
const currentPath = shallowRef("/");
const currentEntry = shallowRef<EditorFileBrowserEntry | null>(null);
const entries = shallowRef<EditorFileBrowserEntry[]>([]);
const selectedEntry = shallowRef<EditorFileBrowserEntry | null>(null);
const loading = shallowRef(false);
const loadError = shallowRef<string | null>(null);
const entryLoaded = shallowRef(false);
const saveAsRelative = shallowRef(false);
let loadSequence = 0;

const scopeOptions = computed(() => {
  const unique = [...new Set(props.scopes)]
    .filter((scope): scope is EditorFileBrowserScope =>
      EDITOR_FILE_BROWSER_SCOPES.includes(scope),
    );
  return unique.length ? unique : ["site"];
});

const scopeLocation = computed(() =>
  resolveEditorFileBrowserScope(
    selectedScope.value,
    props.previewCandidateId,
  ),
);

const modeDefinition = computed(
  () => AUTHORING_PATH_FIELD_MODE_DEFINITIONS[props.mode],
);
const selectsFiles = computed(() => modeDefinition.value.selection === "file");
const canUseVirtualCandidateFolder = computed(
  () => scopeLocation.value.candidateRequired
    && modeDefinition.value.preserveFilename,
);
const candidateContextMissing = computed(
  () => scopeLocation.value.candidateRequired,
);

const includePatterns = computed(() =>
  props.includeContentTypes.length
    ? props.includeContentTypes
    : EDITOR_FILE_BROWSER_FILTER_PATTERNS[props.contentTypeFilter],
);

const visibleEntries = computed(() =>
  visibleEditorFileBrowserEntries(entries.value, {
    scope: selectedScope.value,
    previewCandidateId: props.previewCandidateId,
  }),
);

const breadcrumbs = computed(() =>
  buildEditorFileBrowserBreadcrumbs(
    currentPath.value,
    selectedScope.value,
  ),
);

const hasVisibleEntries = computed(() => visibleEntries.value.length > 0);
const canConfirm = computed(() => {
  if (selectsFiles.value) {
    return Boolean(
      selectedEntry.value
      && entryMatchesSelectionFilter(selectedEntry.value),
    );
  }
  if (candidateContextMissing.value) return canUseVirtualCandidateFolder.value;
  return entryLoaded.value && !loadError.value;
});
const absoluteSelectionPath = computed(() => {
  if (selectsFiles.value) {
    if (!selectedEntry.value) return "";
    return toEditorFileBrowserStoredPath(
      selectedEntry.value.path,
      selectedScope.value,
      props.previewCandidateId,
    );
  }
  return currentPath.value;
});
const relativeSelectionPath = computed(() => {
  if (
    selectedScope.value !== "site"
    || !props.resourceDefinitionPath
    || !absoluteSelectionPath.value
  ) {
    return null;
  }
  return toResourceRelativePath(
    absoluteSelectionPath.value,
    props.resourceDefinitionPath,
  );
});
const selectionIsDescendant = computed(() =>
  selectedScope.value === "site"
  && isResourcePathDescendant(
    absoluteSelectionPath.value,
    props.resourceDefinitionPath,
  ),
);
const storedSelectionPath = computed(() => {
  if (selectedScope.value !== "site") return absoluteSelectionPath.value;
  return toStoredResourceSelectionPath(
    absoluteSelectionPath.value,
    props.resourceDefinitionPath,
    saveAsRelative.value,
  );
});

const confirmLabel = computed(() => selectsFiles.value ? "Select file" : "Select folder");
const dialogTitle = computed(() =>
  props.title || (selectsFiles.value ? "Choose a file" : "Choose a folder"),
);
const dialogDescription = computed(() =>
  props.description
  || (selectsFiles.value
    ? "Browse and select an existing site file."
    : "Browse to the folder this field should use."),
);

function scopeLabel(scope: EditorFileBrowserScope): string {
  return resolveEditorFileBrowserScope(scope).label;
}

function entryName(entry: EditorFileBrowserEntry): string {
  return entry.name || entry.path.split("/").filter(Boolean).at(-1) || entry.path;
}

function formatSize(length?: number | null): string {
  if (length == null) return "";
  if (length < 1024) return `${length} B`;
  if (length < 1024 * 1024) return `${(length / 1024).toFixed(1)} KB`;
  return `${(length / (1024 * 1024)).toFixed(1)} MB`;
}

function entryMatchesSelectionFilter(entry: EditorFileBrowserEntry): boolean {
  return editorFileBrowserEntryMatchesFilter(
    entry,
    includePatterns.value,
    props.excludeContentTypes,
  );
}

async function loadEntry(storedPath: string, selectCurrent = false) {
  const sequence = ++loadSequence;
  currentEntry.value = null;
  selectedEntry.value = null;
  saveAsRelative.value = false;
  loadError.value = null;
  entryLoaded.value = false;
  entries.value = [];

  const loadingPath = toEditorFileBrowserLoadingPath(
    storedPath,
    selectedScope.value,
    props.previewCandidateId,
    props.resourceDefinitionPath,
  );
  currentPath.value = storedPath;

  if (!loadingPath) {
    if (!canUseVirtualCandidateFolder.value) {
      loadError.value =
        "Select a preview candidate before browsing current-candidate files.";
    }
    return;
  }

  loading.value = true;
  try {
    const loaded = await fileEntriesApi.get(
      toFileApiPath(loadingPath),
      "flags,metadata",
      { suppressNotFoundToast: true },
    );
    if (sequence !== loadSequence) return;

    if (!loaded) {
      const message = fileEntriesApi.error.value?.message;
      loadError.value = message || "This node could not be loaded.";
      return;
    }

    currentPath.value = toEditorFileBrowserStoredPath(
      loadingPath,
      selectedScope.value,
      props.previewCandidateId,
    );
    const loadedCurrentEntry = loaded as EditorFileBrowserEntry;
    currentEntry.value = loadedCurrentEntry;
    entries.value = (loaded.children ?? []) as EditorFileBrowserEntry[];
    entryLoaded.value = true;
    if (
      selectCurrent
      && selectsFiles.value
      && entryMatchesSelectionFilter(loadedCurrentEntry)
    ) {
      selectedEntry.value = loadedCurrentEntry;
    }
  } catch (error) {
    if (sequence !== loadSequence) return;
    loadError.value = error instanceof Error
      ? error.message
      : "This node could not be loaded.";
  } finally {
    if (sequence === loadSequence) loading.value = false;
  }
}

function initialize() {
  const inferredScope = inferEditorFileBrowserScope(
    props.path,
    props.defaultScope,
  );
  selectedScope.value = scopeOptions.value.includes(inferredScope)
    ? inferredScope
    : scopeOptions.value[0]!;
  currentPath.value = initialEditorFileBrowserFolder(
    props.path,
    props.mode,
    selectedScope.value,
    props.previewCandidateId,
    props.resourceDefinitionPath,
  );
  loadEntry(currentPath.value);
}

function changeScope(value: unknown) {
  if (
    typeof value !== "string"
    || !scopeOptions.value.includes(value as EditorFileBrowserScope)
  ) {
    return;
  }
  selectedScope.value = value as EditorFileBrowserScope;
  loadEntry(scopeLocation.value.storedRoot);
}

function openEntry(entry: EditorFileBrowserEntry) {
  loadEntry(
    toEditorFileBrowserStoredPath(
      entry.path,
      selectedScope.value,
      props.previewCandidateId,
    ),
    true,
  );
}

function confirm() {
  if (!canConfirm.value) return;

  const kind = selectsFiles.value ? "file" : "folder";
  const entry = kind === "file" ? selectedEntry.value : null;
  const path = storedSelectionPath.value;
  const selection: EditorFileBrowserSelection = {
    path,
    scope: selectedScope.value,
    kind,
    entry,
  };

  emit("select", path, selection);
  open.value = false;
}

function cancel() {
  emit("cancel");
  open.value = false;
}

watch(open, (isOpen) => {
  if (isOpen) {
    initialize();
    return;
  }
  loadSequence++;
  loading.value = false;
}, { immediate: true });

watch(
  () => props.previewCandidateId,
  () => {
    if (open.value && selectedScope.value === "user") initialize();
  },
);

watch(
  [
    absoluteSelectionPath,
    () => props.resourceDefinitionPath,
    () => selectedScope.value,
  ],
  () => {
    saveAsRelative.value = selectionIsDescendant.value;
  },
);
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent
      class="grid max-h-[82dvh] grid-rows-[auto_auto_minmax(280px,1fr)_auto_auto] gap-0 overflow-hidden p-0 sm:max-w-[680px]"
    >
      <DialogHeader class="border-b px-5 py-4">
        <DialogTitle>{{ dialogTitle }}</DialogTitle>
        <DialogDescription>{{ dialogDescription }}</DialogDescription>
      </DialogHeader>

      <div class="flex items-center gap-3 border-b bg-muted/35 px-5 py-3">
        <Label for="editor-file-browser-scope" class="shrink-0 text-[12px]">
          Location
        </Label>
        <Select
          :model-value="selectedScope"
          :disabled="scopeOptions.length === 1"
          @update:model-value="changeScope"
        >
          <SelectTrigger
            id="editor-file-browser-scope"
            class="h-8 min-w-44 rounded-sm bg-background text-[13px]"
            data-testid="file-browser-scope"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="scope in scopeOptions"
              :key="scope"
              :value="scope"
            >
              {{ scopeLabel(scope) }}
            </SelectItem>
          </SelectContent>
        </Select>

        <div class="ml-auto min-w-0 truncate font-mono text-[11px] text-muted-foreground">
          {{ currentPath }}
        </div>
      </div>

      <div class="flex min-h-0 flex-col">
        <nav
          class="flex min-h-10 items-center gap-0.5 overflow-x-auto border-b px-3"
          aria-label="File browser breadcrumbs"
        >
          <template
            v-for="(breadcrumb, index) in breadcrumbs"
            :key="breadcrumb.path"
          >
            <Icon
              v-if="index > 0"
              name="lucide:chevron-right"
              class="size-3.5 shrink-0 text-muted-foreground"
            />
            <Button
              variant="ghost"
              size="sm"
              class="h-7 shrink-0 rounded-sm px-2 text-[12px]"
              :disabled="loading || index === breadcrumbs.length - 1"
              @click="loadEntry(breadcrumb.path, true)"
            >
              {{ breadcrumb.label }}
            </Button>
          </template>
        </nav>

        <div class="min-h-0 flex-1 overflow-y-auto p-3">
          <div
            v-if="loading"
            class="space-y-2"
            aria-label="Loading files"
          >
            <Skeleton v-for="index in 5" :key="index" class="h-11 w-full rounded-sm" />
          </div>

          <Alert
            v-else-if="candidateContextMissing"
            class="my-2"
            data-testid="file-browser-missing-candidate"
          >
            <Icon name="lucide:user-round-search" />
            <AlertTitle>Preview candidate required</AlertTitle>
            <AlertDescription v-if="canUseVirtualCandidateFolder">
              Existing candidate files cannot be browsed without a preview
              candidate. You can still confirm the tokenized output folder
              shown above.
            </AlertDescription>
            <AlertDescription v-else>
              Select a preview candidate before browsing current-candidate
              files. No candidate directory has been guessed or opened.
            </AlertDescription>
          </Alert>

          <Alert
            v-else-if="loadError"
            variant="destructive"
            class="my-2"
            data-testid="file-browser-error"
          >
            <Icon name="lucide:triangle-alert" />
            <AlertTitle>Unable to load this node</AlertTitle>
            <AlertDescription>{{ loadError }}</AlertDescription>
          </Alert>

          <div v-else-if="hasVisibleEntries" class="space-y-1">
            <button
              v-for="entry in visibleEntries"
              :key="entry.id || entry.path"
              type="button"
              class="flex w-full items-center gap-3 rounded-sm border border-transparent px-3 py-2 text-left hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              @click="openEntry(entry)"
            >
              <span class="flex size-8 shrink-0 items-center justify-center rounded-sm bg-muted text-muted-foreground">
                <Icon
                  :name="isEditorFileBrowserDirectory(entry) ? 'lucide:folder' : 'lucide:file'"
                  class="size-4"
                />
              </span>
              <span class="min-w-0 flex-1">
                <span class="block truncate text-[13px] font-medium">
                  {{ entryName(entry) }}
                </span>
                <span class="block truncate text-[10.5px] text-muted-foreground">
                  {{ entry.type }}
                </span>
              </span>
              <span
                v-if="selectsFiles && !entryMatchesSelectionFilter(entry)"
                class="shrink-0 text-[10.5px] font-medium text-muted-foreground"
              >
                Browse only
              </span>
              <span
                v-if="formatSize(entry.length)"
                class="shrink-0 text-[11px] tabular-nums text-muted-foreground"
              >
                {{ formatSize(entry.length) }}
              </span>
              <Icon name="lucide:chevron-right" class="size-4 shrink-0 text-muted-foreground" />
            </button>
          </div>

          <div
            v-else
            class="flex min-h-52 flex-col items-center justify-center gap-2 rounded-md border border-dashed px-6 text-center"
            data-testid="file-browser-empty"
          >
            <span class="flex size-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Icon name="lucide:folder-open" class="size-4" />
            </span>
            <p class="text-[13px] font-medium">
              This node has no children
            </p>
            <p class="max-w-[40ch] text-[12px] text-muted-foreground">
              You can select this node if it matches the requested content type.
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="resourceDefinitionPath && selectedScope === 'site'"
        class="space-y-2 border-t bg-muted/20 px-5 py-3"
      >
        <label
          class="flex cursor-pointer items-center gap-2 text-[12.5px]"
        >
          <Checkbox
            :model-value="saveAsRelative"
            :disabled="!canConfirm || !relativeSelectionPath"
            @update:model-value="saveAsRelative = $event === true"
          />
          Save as relative path
        </label>
        <p
          v-if="canConfirm"
          class="truncate font-mono text-[11px] text-muted-foreground"
        >
          Save as: {{ storedSelectionPath }}
        </p>
      </div>

      <DialogFooter class="border-t px-5 py-4">
        <Button variant="outline" @click="cancel">Cancel</Button>
        <Button
          :disabled="!canConfirm || loading"
          data-testid="file-browser-confirm"
          @click="confirm"
        >
          {{ confirmLabel }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
